import { Card, CardHeader, CardBody, CardFooter, Button } from '@/shared/components/ui';
import { User, TrendingUp, ShoppingCart, DollarSign } from 'lucide-react';
import styles from './CardsPage.module.css';

export const CardsPage = () => {
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Cards</h1>
                <p className={styles.description}>
                    Componente Card para agrupar contenido relacionado. Soporta Header, Body y Footer
                    con opciones de personalización y estado clickeable.
                </p>
            </div>

            {/* Card Básico */}
            <section className={styles.section}>
                <h2>Card Básico</h2>
                <p>Card simple con solo contenido.</p>

                <div className={styles.example}>
                    <Card>
                        <CardBody>
                            Este es un card básico con solo contenido en el body.
                            Perfecto para mostrar información simple.
                        </CardBody>
                    </Card>

                    <code className={styles.code}>
                        {`<Card>
  <CardBody>
    Contenido del card...
  </CardBody>
</Card>`}
                    </code>
                </div>
            </section>

            {/* Con Header y Footer */}
            <section className={styles.section}>
                <h2>Card Completo</h2>
                <p>Card con Header, Body y Footer.</p>

                <div className={styles.example}>
                    <Card>
                        <CardHeader>Título del Card</CardHeader>
                        <CardBody>
                            Este es el contenido principal del card. Puede contener cualquier
                            elemento: texto, imágenes, formularios, etc.
                        </CardBody>
                        <CardFooter>
                            <Button variant="secondary" size="sm">Cancelar</Button>
                            <Button variant="primary" size="sm">Guardar</Button>
                        </CardFooter>
                    </Card>

                    <code className={styles.code}>
                        {`<Card>
  <CardHeader>Título del Card</CardHeader>
  <CardBody>
    Contenido principal...
  </CardBody>
  <CardFooter>
    <Button variant="secondary">Cancelar</Button>
    <Button variant="primary">Guardar</Button>
  </CardFooter>
</Card>`}
                    </code>
                </div>
            </section>

            {/* Con Acciones */}
            <section className={styles.section}>
                <h2>Card con Acciones</h2>
                <p>Header con acciones en el lado derecho.</p>

                <div className={styles.example}>
                    <Card>
                        <CardHeader actions={
                            <>
                                <Button size="sm" variant="secondary">Editar</Button>
                                <Button size="sm" variant="danger">Eliminar</Button>
                            </>
                        }>
                            Usuario: Juan Pérez
                        </CardHeader>
                        <CardBody>
                            <p><strong>Email:</strong> juan@ejemplo.com</p>
                            <p><strong>Rol:</strong> Administrador</p>
                            <p><strong>Estado:</strong> Activo</p>
                        </CardBody>
                    </Card>

                    <code className={styles.code}>
                        {`<Card>
  <CardHeader actions={
    <>
      <Button size="sm">Editar</Button>
      <Button size="sm" variant="danger">Eliminar</Button>
    </>
  }>
    Usuario: Juan Pérez
  </CardHeader>
  <CardBody>
    {/* Contenido... */}
  </CardBody>
</Card>`}
                    </code>
                </div>
            </section>

            {/* Cards Clickeables */}
            <section className={styles.section}>
                <h2>Cards Clickeables</h2>
                <p>Cards que actúan como enlaces o triggers de acciones.</p>

                <div className={styles.cardGrid}>
                    <Card onClick={() => alert('Card 1 clicked!')}>
                        <CardBody>
                            <div style={{ textAlign: 'center' }}>
                                <User size={48} style={{ marginBottom: '1rem', color: 'var(--color-primary)' }} />
                                <h3 style={{ margin: '0 0 0.5rem 0' }}>125</h3>
                                <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>Usuarios Activos</p>
                            </div>
                        </CardBody>
                    </Card>

                    <Card onClick={() => alert('Card 2 clicked!')}>
                        <CardBody>
                            <div style={{ textAlign: 'center' }}>
                                <ShoppingCart size={48} style={{ marginBottom: '1rem', color: 'var(--color-success)' }} />
                                <h3 style={{ margin: '0 0 0.5rem 0' }}>89</h3>
                                <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>Pedidos Hoy</p>
                            </div>
                        </CardBody>
                    </Card>

                    <Card onClick={() => alert('Card 3 clicked!')}>
                        <CardBody>
                            <div style={{ textAlign: 'center' }}>
                                <TrendingUp size={48} style={{ marginBottom: '1rem', color: 'var(--color-info)' }} />
                                <h3 style={{ margin: '0 0 0.5rem 0' }}>+24%</h3>
                                <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>Crecimiento</p>
                            </div>
                        </CardBody>
                    </Card>

                    <Card onClick={() => alert('Card 4 clicked!')}>
                        <CardBody>
                            <div style={{ textAlign: 'center' }}>
                                <DollarSign size={48} style={{ marginBottom: '1rem', color: 'var(--color-warning)' }} />
                                <h3 style={{ margin: '0 0 0.5rem 0' }}>$45,2K</h3>
                                <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>Ingresos</p>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                <code className={styles.code}>
                    {`<Card onClick={() => handleClick()}>
  <CardBody>
    <div style={{ textAlign: 'center' }}>
      <User size={48} />
      <h3>125</h3>
      <p>Usuarios Activos</p>
    </div>
  </CardBody>
</Card>`}
                </code>
            </section>

            {/* Grid de Cards */}
            <section className={styles.section}>
                <h2>Grid de Cards</h2>
                <p>Layout responsive con múltiples cards.</p>

                <div className={styles.cardGrid}>
                    <Card>
                        <CardHeader>Proyecto A</CardHeader>
                        <CardBody>
                            <p>Estado: En Progreso</p>
                            <p>Progreso: 65%</p>
                        </CardBody>
                        <CardFooter>
                            <Button size="sm" fullWidth>Ver Detalles</Button>
                        </CardFooter>
                    </Card>

                    <Card>
                        <CardHeader>Proyecto B</CardHeader>
                        <CardBody>
                            <p>Estado: Completado</p>
                            <p>Progreso: 100%</p>
                        </CardBody>
                        <CardFooter>
                            <Button size="sm" fullWidth variant="success">Ver Detalles</Button>
                        </CardFooter>
                    </Card>

                    <Card>
                        <CardHeader>Proyecto C</CardHeader>
                        <CardBody>
                            <p>Estado: Pendiente</p>
                            <p>Progreso: 0%</p>
                        </CardBody>
                        <CardFooter>
                            <Button size="sm" fullWidth variant="secondary">Ver Detalles</Button>
                        </CardFooter>
                    </Card>
                </div>
            </section>

            {/* Props */}
            <section className={styles.section}>
                <h2>Props</h2>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Componente</th>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>Card</code></td>
                                <td><code>onClick</code></td>
                                <td><code>() =&gt; void</code></td>
                                <td>Hace el card clickeable</td>
                            </tr>
                            <tr>
                                <td><code>CardHeader</code></td>
                                <td><code>actions</code></td>
                                <td><code>ReactNode</code></td>
                                <td>Acciones/botones en el header</td>
                            </tr>
                            <tr>
                                <td><code>All</code></td>
                                <td><code>children</code></td>
                                <td><code>ReactNode</code></td>
                                <td>Contenido del componente</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Import */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`import { Card, CardHeader, CardBody, CardFooter } from '@/shared/components/ui';`}
                </code>
            </section>
        </div>
    );
};
